import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiChat, type MemoryEntry, type UserPreferences } from '@/lib/api';
import {
  Brain,
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  Tag,
  Settings2,
} from 'lucide-react';

const CATEGORIES: { value: MemoryEntry['category']; label: string; color: string }[] = [
  { value: 'preference', label: 'Preference', color: 'text-blue-400 bg-blue-400/10' },
  { value: 'fact', label: 'Fact', color: 'text-green-400 bg-green-400/10' },
  { value: 'instruction', label: 'Instruction', color: 'text-amber-400 bg-amber-400/10' },
  { value: 'context', label: 'Context', color: 'text-purple-400 bg-purple-400/10' },
];

const RESPONSE_STYLES: { value: UserPreferences['response_style']; label: string; description: string }[] = [
  { value: 'concise', label: 'Concise', description: 'Short, direct answers' },
  { value: 'balanced', label: 'Balanced', description: 'Moderate detail' },
  { value: 'detailed', label: 'Detailed', description: 'Thorough explanations' },
  { value: 'technical', label: 'Technical', description: 'Deep technical depth' },
];

export function AIMemory() {
  const queryClient = useQueryClient();
  const [newMemory, setNewMemory] = useState('');
  const [newCategory, setNewCategory] = useState<MemoryEntry['category']>('fact');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [clientInput, setClientInput] = useState('');

  // ─── Queries ────────────────────────────────────────────────────────────────

  const { data, isLoading } = useQuery({
    queryKey: ['ai-memory'],
    queryFn: () => aiChat.memory.get(),
  });

  const memories = data?.memories ?? [];
  const preferences = data?.preferences ?? {
    response_style: 'balanced' as const,
    timezone: '',
    role: '',
    specialties: [],
    clients_managed: [],
  };

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const addMemory = useMutation({
    mutationFn: (body: { content: string; category: string }) => aiChat.memory.add(body.content, body.category),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ai-memory'] }); setNewMemory(''); },
  });

  const updateMemory = useMutation({
    mutationFn: (args: { id: string; content: string }) => aiChat.memory.update(args.id, { content: args.content }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ai-memory'] }); setEditingId(null); },
  });

  const deleteMemory = useMutation({
    mutationFn: (id: string) => aiChat.memory.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-memory'] }),
  });

  const clearMemories = useMutation({
    mutationFn: () => aiChat.memory.clear(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-memory'] }),
  });

  const updatePreferences = useMutation({
    mutationFn: (prefs: Partial<UserPreferences>) => aiChat.memory.updatePreferences(prefs),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-memory'] }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Loading memory...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Brain className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">AI Memory</h1>
            <p className="text-sm text-muted-foreground">
              Manage what The One AI remembers about you across conversations
            </p>
          </div>
        </div>
      </div>

      {/* Preferences */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Preferences</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Response Style */}
          <div>
            <label className="block text-sm text-muted-foreground mb-1.5">Response Style</label>
            <div className="grid grid-cols-2 gap-2">
              {RESPONSE_STYLES.map((style) => (
                <button
                  key={style.value}
                  onClick={() => updatePreferences.mutate({ response_style: style.value })}
                  className={`p-2 rounded-md border text-left text-sm transition-colors ${
                    preferences.response_style === style.value
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <div className="font-medium">{style.label}</div>
                  <div className="text-xs text-muted-foreground">{style.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Role */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Your Role</label>
              <input
                type="text"
                value={preferences.role}
                onChange={(e) => updatePreferences.mutate({ role: e.target.value })}
                placeholder="e.g., IT Director, Level 2 Tech"
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Timezone</label>
              <input
                type="text"
                value={preferences.timezone}
                onChange={(e) => updatePreferences.mutate({ timezone: e.target.value })}
                placeholder="e.g., America/New_York"
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
              />
            </div>
          </div>
        </div>

        {/* Specialties */}
        <div>
          <label className="block text-sm text-muted-foreground mb-1.5">Specialties</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {preferences.specialties.map((s) => (
              <span key={s} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-xs">
                {s}
                <button
                  onClick={() => updatePreferences.mutate({ specialties: preferences.specialties.filter((x) => x !== s) })}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={specialtyInput}
              onChange={(e) => setSpecialtyInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && specialtyInput.trim()) {
                  updatePreferences.mutate({ specialties: [...preferences.specialties, specialtyInput.trim()] });
                  setSpecialtyInput('');
                }
              }}
              placeholder="Add specialty..."
              className="flex-1 px-3 py-1.5 rounded-md border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* Clients */}
        <div>
          <label className="block text-sm text-muted-foreground mb-1.5">Key Clients</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {preferences.clients_managed.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-accent text-foreground text-xs">
                {c}
                <button
                  onClick={() => updatePreferences.mutate({ clients_managed: preferences.clients_managed.filter((x) => x !== c) })}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={clientInput}
              onChange={(e) => setClientInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && clientInput.trim()) {
                  updatePreferences.mutate({ clients_managed: [...preferences.clients_managed, clientInput.trim()] });
                  setClientInput('');
                }
              }}
              placeholder="Add client..."
              className="flex-1 px-3 py-1.5 rounded-md border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
            />
          </div>
        </div>
      </section>

      {/* Memories */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Memories ({memories.length}/100)</h2>
          </div>
          {memories.length > 0 && (
            <button
              onClick={() => { if (confirm('Clear all memories?')) clearMemories.mutate(); }}
              className="text-xs text-destructive hover:text-destructive/80 flex items-center gap-1"
            >
              <Trash2 className="h-3 w-3" />
              Clear All
            </button>
          )}
        </div>

        {/* Add memory */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newMemory}
            onChange={(e) => setNewMemory(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newMemory.trim()) {
                addMemory.mutate({ content: newMemory.trim(), category: newCategory });
              }
            }}
            placeholder='e.g., "I prefer PowerShell over bash"'
            className="flex-1 px-3 py-2 rounded-md border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
          />
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value as MemoryEntry['category'])}
            className="px-2 py-2 rounded-md border border-border bg-background text-sm text-foreground"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <button
            onClick={() => { if (newMemory.trim()) addMemory.mutate({ content: newMemory.trim(), category: newCategory }); }}
            disabled={!newMemory.trim() || addMemory.isPending}
            className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>

        {/* Memory list */}
        <div className="space-y-2">
          {memories.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <Brain className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p>No memories yet. Add memories manually or say "remember that..." in chat.</p>
            </div>
          )}

          {memories.map((memory) => {
            const cat = CATEGORIES.find((c) => c.value === memory.category);
            const isEditing = editingId === memory.id;

            return (
              <div
                key={memory.id}
                className="flex items-start gap-3 px-3 py-2.5 rounded-md border border-border bg-background group"
              >
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="flex-1 px-2 py-1 rounded border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') updateMemory.mutate({ id: memory.id, content: editContent });
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                      <button onClick={() => updateMemory.mutate({ id: memory.id, content: editContent })} className="p-1 text-green-500 hover:text-green-400">
                        <Save className="h-4 w-4" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-1 text-muted-foreground hover:text-foreground">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-foreground">{memory.content}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${cat?.color ?? 'text-muted-foreground bg-muted'}`}>
                          <Tag className="h-2.5 w-2.5" />
                          {cat?.label ?? memory.category}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {memory.source === 'inferred' ? `inferred (${Math.round(memory.confidence * 100)}%)` : 'explicit'}
                        </span>
                        <span className="text-xs text-muted-foreground">{new Date(memory.created_at).toLocaleDateString()}</span>
                      </div>
                    </>
                  )}
                </div>

                {!isEditing && (
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                    <button
                      onClick={() => { setEditingId(memory.id); setEditContent(memory.content); }}
                      className="p-1 rounded hover:bg-accent text-muted-foreground"
                      title="Edit"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => deleteMemory.mutate(memory.id)}
                      className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
